import moment from 'moment'
import { map } from 'lodash'

// 类型定义
export interface DateRange {
  start?: Date | string | number;
  end?: Date | string | number;
}

export interface ParsedDateRange {
  start: number | undefined;
  end: number | undefined;
}

export interface MoneyObject {
  standardUnit: string;
  standardScale: number;
  standardSymbol: string;
  standardNumCode: string;
  standardStrCode: string;
  standard?: string;
}

export type DateValue = Date | string | number | null | undefined;
export type NumberValue = number | string | null | undefined;
export type TextValue = string | number | object | any[] | null | undefined;
export type AIResultValue = DateValue | DateRange | NumberValue | TextValue | object | any[];

export interface TypeMap {
  [key: string]: string;
}

export interface AIResult {
  [key: string]: AIResultValue;
}

/**
 * 解析各种格式的日期为时间戳
 * @param dateValue - 日期值(字符串、Date对象、时间戳等)
 * @returns 返回时间戳或undefined
 */
const parseDateToTimestamp = (dateValue: DateValue): number | undefined => {
  if (!dateValue) return undefined

  // 如果是Date对象
  if (dateValue instanceof Date) {
    return isNaN(dateValue.getTime()) ? undefined : dateValue.getTime();
  }
  
  // 如果已经是有效时间戳
  if (typeof dateValue === 'number' && !isNaN(dateValue)) {
    return dateValue;
  }
  
  // 字符串处理
  if (typeof dateValue === 'string' && dateValue.trim()) {
    const trimmedValue = dateValue.trim();
    
    // 处理"YYYY年MM月DD日"格式
    if (/^\d{4}年\d{1,2}月\d{1,2}日$/.test(trimmedValue)) {
      const parts = trimmedValue.match(/(\d{4})年(\d{1,2})月(\d{1,2})日/);
      if (parts) {
        const date = new Date(parseInt(parts[1]), parseInt(parts[2]) - 1, parseInt(parts[3]));
        return isNaN(date.getTime()) ? undefined : date.getTime();
      }
    }
    
    // 尝试标准解析
    let date = new Date(trimmedValue);
    if (!isNaN(date.getTime())) {
      return date.getTime();
    }
    
    // 尝试解析"YYYY-MM-DD"、"YYYY/MM/DD"等格式
    // 优化正则表达式以兼容低版本Safari浏览器
    const dateRegex = /(\d{4})(?:[-/]|年)(\d{1,2})(?:[-/]|月)(\d{1,2})(?:日)?/;
    const match = trimmedValue.match(dateRegex);
    if (match) {
      date = new Date(parseInt(match[1]), parseInt(match[2]) - 1, parseInt(match[3]));
      return isNaN(date.getTime()) ? undefined : date.getTime();
    }
  }
  
  return undefined;
};


/**
 * 解析日期范围对象
 * @param rangeValue - 日期范围值
 * @returns 返回日期范围或null
 */
const parseDateRange = (rangeValue: any): ParsedDateRange | null => {
  if (!rangeValue || typeof rangeValue !== 'object') {
    return null;
  }
  
  let start, end;
  
  // 处理start日期
  if ('start' in rangeValue) {
    start = parseDateToTimestamp(rangeValue.start);
  }
  
  // 处理end日期
  if ('end' in rangeValue) {
    end = parseDateToTimestamp(rangeValue.end);
  }
  
  // 至少需要一个有效日期
  if (start === undefined && end === undefined) {
    return null;
  }
  
  return { start, end };
};

/**
 * 解析数字值
 * @param numValue - 数字值
 * @returns 返回数字或undefined
 */
const parseNumberValue = (numValue: NumberValue): number | undefined => {
  if (numValue === null || numValue === undefined) {
    return undefined;
  }
  
  if (typeof numValue === 'number' && !isNaN(numValue)) {
    return numValue;
  }
  
  if (typeof numValue === 'string' && numValue.trim()) {
    // 移除逗号、空格等非数字字符（保留小数点和负号）
    const cleanedStr = numValue.replace(/[^\d.-]/g, '');
    const num = parseFloat(cleanedStr);
    return isNaN(num) ? undefined : num;
  }
  
  return undefined;
};

/**
 * 格式化对象为字符串
 * @param obj - 要格式化的对象
 * @returns 格式化后的字符串
 */
const formatObjectToString = (obj: any): string => {
  if (!obj || typeof obj !== 'object') return String(obj);
  
  try {
    const parts = Object.entries(obj).map(([key, val]) => {
      const displayVal = typeof val === 'string' ? `'${val}'` : val;
      return `${key}:${displayVal}`;
    });
    return parts.join(',');
  } catch (e) {
    console.error('解析对象失败:', e);
    return String(obj);
  }
};

/**
 * 格式化数组为字符串
 * @param arr - 要格式化的数组
 * @returns 格式化后的字符串
 */
const formatArrayToString = (arr: any[]): string => {
  if (!Array.isArray(arr)) return String(arr);
  
  try {
    // 处理对象数组
    if (arr.length > 0 && typeof arr[0] === 'object' && arr[0] !== null) {
      const flatPairs = arr.flatMap(obj => {
        if (obj && typeof obj === 'object') {
          return Object.entries(obj).map(([key, val]) => {
            const displayVal = typeof val === 'string' ? `'${val}'` : val;
            return `${key}:${displayVal}`;
          });
        }
        return [];
      });
      return flatPairs.join('，');
    }
    
    // 处理普通数组
    return arr.join('，');
  } catch (e) {
    console.error('解析数组失败:', e);
    return String(arr);
  }
};

/**
 * 获取基础货币对象
 * @returns 返回标准货币对象
 */
const getBaseMoney = (): MoneyObject => ({
  standardUnit: '元',
  standardScale: 2,
  standardSymbol: '¥',
  standardNumCode: '156',
  standardStrCode: 'CNY'
});

/**
 * 解析AI结果值为指定类型
 * @param value - 要解析的值
 * @param type - 目标类型
 * @returns 解析后的值
 */
export const parseAIResultValue = (value: AIResultValue, type: string): any => {
  try {
    // 日期类型
    if (type === 'date') {
      // 处理日期
      const timestamp = parseDateToTimestamp(value as DateValue);
      
      // 如果是个日期范围，则显示开始值
      if (typeof value === 'object' && value !== null && 'start' in value && 'end' in value) {
        return parseDateToTimestamp((value as DateRange).start);
      }
      
      return timestamp;
    }
    
    // 日期范围类型
    if (type === 'dateRange') {
      return  parseDateRange(value);
    }

    // 数字类型
    if (type === 'number') {
      return parseNumberValue(value as NumberValue);
    }
    
    // 金额类型
    if (type === 'money') {
      const baseMoney = getBaseMoney();
      
      // 如果已经是money对象格式
      if (typeof value === 'object' && value !== null && 'standard' in value) {
        return value;
      }
      
      // 解析为数字
      const numValue = parseNumberValue(value as NumberValue);
      if (numValue === null || numValue === undefined) return undefined;
      
      return {
        ...baseMoney,
        standard: typeof numValue === 'string' ? numValue : numValue.toString()
      };
    }
    
    // 文本类型
    if (type === 'text') {
      // null或undefined
      if (value === null || value === undefined) {
        return '';
      }
      
      // 字符串直接返回
      if (typeof value === 'string') {
        return value;
      }
      
      // 对象格式
      if (typeof value === 'object' && !Array.isArray(value) && value !== null) {
        return formatObjectToString(value);
      }
      
      // 数组格式
      if (Array.isArray(value)) {
        return formatArrayToString(value);
      }
      
      // 其他类型
      return String(value);
    }

    if(type === 'list:dataLinkEdits') {
      return value
    }
    
    // 默认情况
    return value;
  } catch (error) {
    console.error('解析AI结果值失败:', error, { value, type });
    return null;
  }
};

/**
 * 提取拼接输入内容的名称
 * @param {*} input 
 * @returns 
 */
export const extractNames = (input:any) => {
  let data = input;

  if (typeof input === 'string') {
    try {
      data = JSON.parse(input);
    } catch {
      return '';
    }
  }

  if (!data) return '';

  if (Array.isArray(data)) {
    return data
      .map(({ name, label }) => name ?? label ?? '')
      .filter(Boolean)
      .join('、');
  }

  if (typeof data === 'object') {
    return data.name ?? data.label ?? '';
  }

  return '';
}

// 将格式化后的数据转为字符串，用于显示识别结果
export const formatAIResultToString = (result: AIResult, key: string, typeMap: TypeMap): string => {
  const value = result[key]
  const type = typeMap[key]
  const parsedValue = parseAIResultValue(value, type)

  const getDateValue = (dateValue: number | undefined): string => {
    return dateValue ? moment(dateValue).format('YYYY-MM-DD') : ''
  }

  if (type === 'date') {
    return getDateValue(parsedValue)
  }
  if (type === 'dateRange') {
    return getDateValue(parsedValue?.start) + ' - ' + getDateValue(parsedValue?.end)
  }
  if (type === 'money') {
    return parsedValue ? parsedValue?.standard : ''
  } 


  if (
    type?.startsWith('datalink.DataLinkEntity') ||
    type?.startsWith('basedata.Dimensio') ||
    type === 'organization.Staff' ||
    type?.startsWith('basedata.Enum') ||
    type === 'organization.Department' ||
    type === 'basedata.city' ||
    type === 'payeeInfo'
  ) {
    return extractNames(parsedValue)
  }

  return parsedValue ? (typeof parsedValue === 'object' ? JSON.stringify(parsedValue) : String(parsedValue)) : ''
}

// 将格式化后的数据转为dom，用于显示识别结果, 比如业务对象多选
export const formatAIResultToDom = (result: AIResult, key: string, typeMap: TypeMap): string => {
  const value = result[key]
  const type = typeMap[key]
  const parsedValue = parseAIResultValue(value, type)

  // 业务对象多选
  if (type === 'list:dataLinkEdits') {
    return parsedValue ? String(parsedValue) : ''
  }

  return parsedValue ? String(parsedValue) : ''
}

export const getInvoiceTagColorByType = (type:string) => {
  switch (type) {
    case 'exist':
    case 'existConfirm':
    case 'unify':
      return 'success'
    case 'wait':
      return 'warning'
    case 'noExist':
    case 'noWrite':
      return 'default'
    default:
      return 'default'
  }
}


let _TEM_LIST: FeeType[] = []

export interface FeeType {
  id: string
  name: string
  fullname?: string
  parentId?: string
  children?: FeeType[]
}


export function getFeeTypePath(list:FeeType[], feeType:FeeType) {
  _TEM_LIST = list
  let parents = getFullPath(list, feeType, [])
  parents.unshift(feeType)
  let data = parents.reverse()
  return map(data, item => item?.name).join('/')
}

function getFullPath(list:FeeType[], selectedItem:FeeType, result:FeeType[] = []) {
  list.forEach(el => {
    let node = undefined
    if (el?.id === selectedItem?.parentId) {
      node = el
      result.push(el)
    }

    if (node) {
      getFullPath(_TEM_LIST, node, result)
    } else {
      getFullPath(el.children || [], selectedItem, result)
    }
  })
  return result
}